import { hasTextTag, textTagKeys } from "./i18n";

/** Set on every element that shows wording, naming the message key(s) (space separated) while "Edit text on screen" is on. */
export const TEXT_KEY_ATTRIBUTE = "data-cc-text-key";
/** Put on the toggle and the editor: wording inside is never outlined and clicks on it are left alone. */
export const TEXT_EDITOR_ATTRIBUTE = "data-cc-text-editor";
const EDIT_FLAG = "data-cc-edit-text";
const ATTRIBUTES_WITH_WORDING = ["placeholder", "aria-label", "title", "alt", "aria-description"];
const ATTRIBUTE_SELECTOR = ATTRIBUTES_WITH_WORDING.map((name) => `[${name}]`).join(",");

export interface TextTaggingOptions {
  /** A tagged element was clicked: the keys it shows (in order) and the element. The click's own action is cancelled. */
  onPick: (keys: string[], element: HTMLElement) => void;
}

function add(found: Map<HTMLElement, string[]>, element: HTMLElement | null, value: string): void {
  if (!element || element.closest(`[${TEXT_EDITOR_ATTRIBUTE}]`)) return;
  const keys = found.get(element) ?? [];
  for (const key of textTagKeys(value)) if (!keys.includes(key)) keys.push(key);
  if (keys.length > 0) found.set(element, keys);
}

/** Finds every element under `root` whose text or attributes carry a key tag and marks it with `data-cc-text-key`; clears stale marks. */
export function scanTextTags(root: HTMLElement): void {
  const found = new Map<HTMLElement, string[]>();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (hasTextTag(node.nodeValue)) add(found, node.parentElement, node.nodeValue ?? "");
  }
  for (const element of root.querySelectorAll<HTMLElement>(ATTRIBUTE_SELECTOR)) {
    for (const name of ATTRIBUTES_WITH_WORDING) {
      const value = element.getAttribute(name);
      if (hasTextTag(value)) add(found, element, value ?? "");
    }
  }
  for (const element of root.querySelectorAll<HTMLElement>(`[${TEXT_KEY_ATTRIBUTE}]`)) {
    if (!found.has(element)) element.removeAttribute(TEXT_KEY_ATTRIBUTE);
  }
  for (const [element, keys] of found) {
    const value = keys.join(" ");
    if (element.getAttribute(TEXT_KEY_ATTRIBUTE) !== value) element.setAttribute(TEXT_KEY_ATTRIBUTE, value);
  }
}

/**
 * Starts "Edit text on screen" on a page: outlines the wording that carries a key tag (the tags only exist while the apps translate in
 * edit mode), keeps the marks current as the page changes, and turns a click on marked wording into `onPick` instead of its usual action.
 * Returns the function that stops it and removes every mark.
 */
export function startTextTagging(root: HTMLElement, options: TextTaggingOptions): () => void {
  let scheduled: ReturnType<typeof setTimeout> | undefined;
  const schedule = () => {
    scheduled ??= setTimeout(() => {
      scheduled = undefined;
      scanTextTags(root);
    }, 30);
  };
  const observer = new MutationObserver(schedule);
  // The marks themselves are attributes outside this filter, so marking does not trigger another scan.
  observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES_WITH_WORDING });

  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(`[${TEXT_EDITOR_ATTRIBUTE}]`)) return;
    const marked = target.closest<HTMLElement>(`[${TEXT_KEY_ATTRIBUTE}]`);
    if (!marked) return;
    event.preventDefault();
    event.stopPropagation();
    options.onPick((marked.getAttribute(TEXT_KEY_ATTRIBUTE) ?? "").split(" ").filter(Boolean), marked);
  };
  document.addEventListener("click", onClick, true);

  root.ownerDocument.documentElement.setAttribute(EDIT_FLAG, "");
  scanTextTags(root);
  schedule();
  return () => {
    observer.disconnect();
    if (scheduled) clearTimeout(scheduled);
    document.removeEventListener("click", onClick, true);
    root.ownerDocument.documentElement.removeAttribute(EDIT_FLAG);
    for (const element of root.querySelectorAll(`[${TEXT_KEY_ATTRIBUTE}]`)) element.removeAttribute(TEXT_KEY_ATTRIBUTE);
  };
}
